//askSmart-ui/src/pages/SMRTDocsEditor.tsx
import React from "react";

export default function SMRTDocsEditor({
  serviceName: _serviceName,
  setServiceName: _setServiceName,
  template,
  setTemplate,
  isEditing = true,
  setIsEditing,
  lockedSteps,
  localTemplate,
  setLocalTemplate,
}: any) {
  const isLocalMode = !!lockedSteps;
  const [snapshot, setSnapshot] = React.useState<any>(null);

  // Drag-to-reorder state for documents (replaces the up/down arrows, which
  // only moved one place per click). A document is dragged by its ⋮ handle;
  // rows are drop targets within the same category. dragStepId scopes the drag
  // so a document cannot be dropped into a different category, and dragOverKey
  // /dragOverPos drive the insert-position indicator.
  const [dragKey, setDragKey] = React.useState<string | null>(null);
  const [dragStepId, setDragStepId] = React.useState<any>(null);
  const [dragOverKey, setDragOverKey] = React.useState<string | null>(null);
  const [dragOverPos, setDragOverPos] = React.useState<"before" | "after">(
    "before",
  );

  const clearDrag = () => {
    setDragKey(null);
    setDragStepId(null);
    setDragOverKey(null);
  };

  // Per-category collapse, keyed by step.id. Collapsing every category reduces
  // the builder to a short list of category rows, which is what makes the
  // category ↑/↓ arrows practical to use.
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});

  const toggleCollapsed = (stepId: any) =>
    setCollapsed((prev) => ({ ...prev, [stepId]: !prev[stepId] }));

  const steps: any[] = template?.steps || [];

  /**
   * Every category actually on screen, whichever mode is running.
   *
   * In local mode none of them come from `template` — the inherited ones come
   * from `lockedSteps` and the service's own from `localTemplate` — so a
   * collapse-all worked out from `steps` alone had nothing to act on, which is
   * why the control used to be hidden in local mode rather than fixed.
   */
  const shownSteps: any[] = isLocalMode
    ? [...(lockedSteps || []), ...(localTemplate?.steps || [])]
    : steps;

  const allCollapsed =
    shownSteps.length > 0 && shownSteps.every((s: any) => collapsed[s.id]);

  const toggleAllCollapsed = () =>
    setCollapsed(
      allCollapsed
        ? {}
        : Object.fromEntries(shownSteps.map((s: any) => [s.id, true])),
    );

  /**
   * Whether this editor owns its own Edit / Save / Cancel.
   *
   * True on a page, false inside the per-user "Local Changes" modal, which has
   * its own pair in its header — a second one in here would be two buttons
   * competing to commit the same thing. `setIsEditing` is what tells them
   * apart: the page passes it, the modal doesn't.
   */
  const ownsEditing = !!setIsEditing;

  // Category reorder renumbers step.id to its 1-based position (see the ↑/↓
  // handlers below), so collapse state keyed by id is really keyed by position.
  // Swap the two entries alongside the steps, or moving a category would leave
  // its collapsed state behind on whichever category took its place.
  const swapCollapsed = (idxA: number, idxB: number) =>
    setCollapsed((prev) => ({
      ...prev,
      [idxA + 1]: !!prev[idxB + 1],
      [idxB + 1]: !!prev[idxA + 1],
    }));

  // Move `fromKey` to just before/after `toKey` within the same category
  // (after = dropped on the lower half of the target row). The item is removed
  // first and `toIdx` is then looked up in the already-spliced array — that is
  // what makes the `+ (after ? 1 : 0)` arithmetic correct when dragging both
  // up and down, with no index adjustment.
  const reorderField = (
    stepId: any,
    fromKey: string,
    toKey: string,
    after: boolean,
  ) => {
    if (fromKey === toKey) return;
    const updated = { ...template };
    updated.steps = updated.steps.map((s: any) => {
      if (s.id !== stepId) return s;
      const fields = [...s.fields];
      const fromIdx = fields.findIndex((f: any) => f.key === fromKey);
      if (fromIdx === -1) return s;
      const [moved] = fields.splice(fromIdx, 1);
      const toIdx = fields.findIndex((f: any) => f.key === toKey);
      if (toIdx === -1) return s;
      fields.splice(toIdx + (after ? 1 : 0), 0, moved);
      return { ...s, fields };
    });
    setTemplate(updated);
  };

  /**
   * This service's own version of the inherited lines, by field key.
   *
   * A Sub Service is edited like any other service, and none of it reaches the
   * Master or its siblings. Only the DIFFERENCE is stored, so a line the child
   * hasn't touched still moves when the Master moves it.
   */
  const overrides: Record<string, any> =
    localTemplate?.fieldOverrides && typeof localTemplate.fieldOverrides === "object"
      ? localTemplate.fieldOverrides
      : {};

  /** An inherited line as this service has it: the Master's, plus its own edits. */
  const inheritedField = (field: any) => ({
    ...field,
    ...(overrides[field?.key] || {}),
    key: field?.key,
  });

  const updateInheritedField = (fieldKey: string, patch: any) => {
    if (!localTemplate || !setLocalTemplate) return;
    setLocalTemplate({
      ...localTemplate,
      fieldOverrides: {
        ...overrides,
        [fieldKey]: { ...(overrides[fieldKey] || {}), ...patch },
      },
    });
  };

  const addLocalFieldToStep = (parentStepId: number) => {
    if (!localTemplate || !setLocalTemplate) return;
    const newField = {
      key: `ldoc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: "New Document",
      type: "",
      mandatory: false,
      approvalRequired: false,
      countersignRequired: false,
      parentStepId,
    };
    setLocalTemplate({
      ...localTemplate,
      attachedFields: {
        ...localTemplate.attachedFields,
        [parentStepId]: [...(localTemplate.attachedFields?.[parentStepId] || []), newField],
      },
    });
  };

  const updateAttachedField = (parentStepId: number, fieldKey: string, updates: any) => {
    if (!localTemplate || !setLocalTemplate) return;
    setLocalTemplate({
      ...localTemplate,
      attachedFields: {
        ...localTemplate.attachedFields,
        [parentStepId]: (localTemplate.attachedFields?.[parentStepId] || []).map((f: any) =>
          f.key === fieldKey ? { ...f, ...updates } : f,
        ),
      },
    });
  };

  const deleteAttachedField = (parentStepId: number, fieldKey: string) => {
    if (!localTemplate || !setLocalTemplate) return;
    setLocalTemplate({
      ...localTemplate,
      attachedFields: {
        ...localTemplate.attachedFields,
        [parentStepId]: (localTemplate.attachedFields?.[parentStepId] || []).filter(
          (f: any) => f.key !== fieldKey,
        ),
      },
    });
  };

  const moveAttachedField = (parentStepId: number, fieldKey: string, direction: -1 | 1) => {
    if (!localTemplate || !setLocalTemplate) return;
    const fields = [...(localTemplate.attachedFields?.[parentStepId] || [])];
    const index = fields.findIndex((f: any) => f.key === fieldKey);
    if (index < 0) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
    setLocalTemplate({
      ...localTemplate,
      attachedFields: { ...localTemplate.attachedFields, [parentStepId]: fields },
    });
  };

  const renderFieldEditing = (field: any, stepId: number, isAttached: boolean) => (
    <div className="template-field-actions">
      {(field.type === "template" || field.type === "certificate") && (
        <button
          className={`btn-toggle ${field.mandatory ? "btn-toggle-mandatory-on" : "btn-toggle-off"}`}
          title="Mandatory"
          onClick={() => {
            if (isAttached) {
              updateAttachedField(stepId, field.key, { mandatory: !field.mandatory });
            } else {
              const updated = { ...localTemplate };
              updated.steps = updated.steps.map((s: any) =>
                s.id !== stepId ? s : { ...s, fields: s.fields.map((f: any) => f.key === field.key ? { ...f, mandatory: !f.mandatory } : f) },
              );
              setLocalTemplate(updated);
            }
          }}
        >M</button>
      )}
      {(field.type === "template" || field.type === "certificate") && (
        <button
          className={`btn-toggle ${field.approvalRequired ? "btn-toggle-approval-on" : "btn-toggle-off"}`}
          title="Approval Required"
          onClick={() => {
            if (isAttached) {
              updateAttachedField(stepId, field.key, { approvalRequired: !field.approvalRequired });
            } else {
              const updated = { ...localTemplate };
              updated.steps = updated.steps.map((s: any) =>
                s.id !== stepId ? s : { ...s, fields: s.fields.map((f: any) => f.key === field.key ? { ...f, approvalRequired: !f.approvalRequired } : f) },
              );
              setLocalTemplate(updated);
            }
          }}
        >✔</button>
      )}
      {field.type === "template" && (
        <button
          className={`btn-toggle ${field.countersignRequired ? "btn-toggle-countersign-on" : "btn-toggle-off"}`}
          title="Countersign Required"
          onClick={() => {
            if (isAttached) {
              updateAttachedField(stepId, field.key, { countersignRequired: !field.countersignRequired });
            } else {
              const updated = { ...localTemplate };
              updated.steps = updated.steps.map((s: any) =>
                s.id !== stepId ? s : { ...s, fields: s.fields.map((f: any) => f.key === field.key ? { ...f, countersignRequired: !f.countersignRequired } : f) },
              );
              setLocalTemplate(updated);
            }
          }}
        >✎</button>
      )}
      <button className="btn-move" onClick={() => {
        if (isAttached) moveAttachedField(stepId, field.key, -1);
        else {
          const updated = { ...localTemplate };
          updated.steps = updated.steps.map((s: any) => {
            if (s.id !== stepId) return s;
            const idx = s.fields.findIndex((f: any) => f.key === field.key);
            if (idx === 0) return s;
            const flds = [...s.fields];
            [flds[idx], flds[idx - 1]] = [flds[idx - 1], flds[idx]];
            return { ...s, fields: flds };
          });
          setLocalTemplate(updated);
        }
      }}>↑</button>
      <button className="btn-move" onClick={() => {
        if (isAttached) moveAttachedField(stepId, field.key, 1);
        else {
          const updated = { ...localTemplate };
          updated.steps = updated.steps.map((s: any) => {
            if (s.id !== stepId) return s;
            const idx = s.fields.findIndex((f: any) => f.key === field.key);
            if (idx === s.fields.length - 1) return s;
            const flds = [...s.fields];
            [flds[idx], flds[idx + 1]] = [flds[idx + 1], flds[idx]];
            return { ...s, fields: flds };
          });
          setLocalTemplate(updated);
        }
      }}>↓</button>
      <button className="btn-toggle btn-toggle-delete" title="Delete" onClick={() => {
        if (isAttached) deleteAttachedField(stepId, field.key);
        else {
          const updated = { ...localTemplate };
          updated.steps = updated.steps.map((s: any) =>
            s.id !== stepId ? s : { ...s, fields: s.fields.filter((f: any) => f.key !== field.key) },
          );
          setLocalTemplate(updated);
        }
      }}>🗑</button>
    </div>
  );

  const renderLocalField = (field: any, stepId: number, isAttached: boolean) => (
    <div key={field.key} className="template-field-row">
      <input
        className="manage-input"
        value={field.label}
        disabled={!isEditing}
        onChange={(e: any) => {
          if (isAttached) {
            updateAttachedField(stepId, field.key, { label: e.target.value });
          } else {
            const updated = { ...localTemplate };
            updated.steps = updated.steps.map((s: any) =>
              s.id !== stepId ? s : { ...s, fields: s.fields.map((f: any) => f.key === field.key ? { ...f, label: e.target.value } : f) },
            );
            setLocalTemplate(updated);
          }
        }}
      />
      <select
        className="manage-input"
        value={field.type || ""}
        disabled={!isEditing}
        onChange={(e: any) => {
          if (isAttached) {
            updateAttachedField(stepId, field.key, { type: e.target.value });
          } else {
            const updated = { ...localTemplate };
            updated.steps = updated.steps.map((s: any) =>
              s.id !== stepId ? s : { ...s, fields: s.fields.map((f: any) => f.key === field.key ? { ...f, type: e.target.value } : f) },
            );
            setLocalTemplate(updated);
          }
        }}
      >
        <option value="">- Please Select -</option>
        <option value="info">Info</option>
        <option value="certificate">Certificate</option>
        <option value="template">Template</option>
      </select>
      {/* Read as a sentence when not editing, the same way an inherited line
          reads — the toggles are how a requirement is CHANGED, and showing
          them on a page that isn't in edit mode invites a change nobody meant
          to make. */}
      {isEditing ? (
        renderFieldEditing(field, stepId, isAttached)
      ) : (
        <div className="template-field-actions">
          <div
            className="manage-input smrtdocs-requirements-text"
            style={{ width: "50ch", cursor: "not-allowed", opacity: 0.85 }}
          >
            {[
              field.mandatory ? "Mandatory" : null,
              field.approvalRequired ? "Approval Required" : null,
              field.countersignRequired ? "Countersign Required" : null,
            ]
              .filter(Boolean)
              .join(", ") || "—"}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="template-list smrtdocs-editor">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
          gap: "8px",
        }}
      >
        {shownSteps.length > 0 && (
          <button
            className="edit-icon"
            title={
              allCollapsed
                ? "Expand all categories"
                : "Collapse all categories to reorder them"
            }
            onClick={toggleAllCollapsed}
          >
            {allCollapsed ? "⤢ Expand All" : "⤡ Collapse All"}
          </button>
        )}

        {ownsEditing && !isEditing ? (
          <button
            className="edit-icon"
            onClick={() => {
              // In local mode the editable thing is the service's OWN content,
              // not the template — the template is empty and the inherited
              // steps are read-only. Cancel has to put back whichever one this
              // editor is actually changing.
              setSnapshot(
                JSON.parse(JSON.stringify(isLocalMode ? localTemplate : template)),
              );
              setIsEditing(true);
            }}
          >
            ✏️ Edit
          </button>
        ) : ownsEditing ? (
          <>
            <button
              className="btn-save"
              onClick={() => {
                setIsEditing(false);
              }}
            >
              Save
            </button>

            <button
              className="btn-cancel"
              onClick={() => {
                if (snapshot) {
                  if (isLocalMode) setLocalTemplate(snapshot);
                  else setTemplate(snapshot);
                }
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
          </>
        ) : null}
      </div>

      {/* Locked template steps (local mode) */}
      {isLocalMode && lockedSteps.map((step: any) => (
        <div key={step.id} className="template-card" style={{ marginBottom: "16px" }}>
          {/* The header collapses; the category itself doesn't change — these
              lines belong to the Master, and only it can alter them. */}
          <div className="template-card-header">
            <button
              className="td-collapse-toggle"
              title={collapsed[step.id] ? "Expand category" : "Collapse category"}
              aria-expanded={!collapsed[step.id]}
              onClick={() => toggleCollapsed(step.id)}
            >
              {collapsed[step.id] ? "▸" : "▾"}
            </button>
            <input className="manage-input" value={step.label} disabled style={{ cursor: "not-allowed", opacity: 0.85 }} />
            {collapsed[step.id] && (
              <span className="td-collapsed-count">
                {(step.fields || []).length +
                  (localTemplate?.attachedFields?.[step.id] || []).length}{" "}
                {(step.fields || []).length +
                  (localTemplate?.attachedFields?.[step.id] || []).length ===
                1
                  ? "document"
                  : "documents"}
              </span>
            )}
          </div>
          {!collapsed[step.id] && (
          <div className="template-grid-header">
            <div>Document</div>
            <div>Type</div>
            <div>Actions</div>
          </div>
          )}
          {!collapsed[step.id] && step.fields.map((master: any) => {
            // Hidden by the Master reaches here as a fact, not a choice: no
            // child can put back a line the Master withdrew, so that row stays
            // read-only whatever mode the editor is in.
            const hiddenByMaster = !!master.hidden;
            // Otherwise the line as THIS service has it — the Master's, plus
            // whatever this service has changed about it.
            const field = inheritedField(master);
            const off = hiddenByMaster || !!field.hidden;
            return (
            <div key={field.key} className="template-field-row">
              {/* Dimmed cell by cell rather than on the row: opacity on a
                  parent composites its children with it, so a greyed row would
                  take the H button down with it — and H is the way back. */}
              <input
                className="manage-input"
                value={field.label}
                disabled={!isEditing || hiddenByMaster}
                onChange={(e: any) =>
                  updateInheritedField(field.key, { label: e.target.value })
                }
                style={{
                  textDecoration: off ? "line-through" : "none",
                  opacity: off ? 0.5 : isEditing && !hiddenByMaster ? 1 : 0.85,
                  ...(isEditing && !hiddenByMaster ? {} : { cursor: "not-allowed" }),
                }}
              />
              {isEditing && !hiddenByMaster ? (
                <select
                  className="manage-input"
                  value={field.type || ""}
                  style={{ opacity: off ? 0.5 : 1 }}
                  onChange={(e: any) =>
                    updateInheritedField(field.key, { type: e.target.value })
                  }
                >
                  <option value="">- Please Select -</option>
                  <option value="info">Info</option>
                  <option value="certificate">Certificate</option>
                  <option value="template">Template</option>
                </select>
              ) : (
                <div className="manage-input" style={{ cursor: "not-allowed", opacity: off ? 0.5 : 0.85 }}>
                  {field.type ? field.type.charAt(0).toUpperCase() + field.type.slice(1) : "—"}
                </div>
              )}
              {/* The requirements read as a sentence until Edit, then give way
                  to the actions — the same swap every other row makes.

                  A line the Master has hidden keeps the sentence even in edit
                  mode: there is genuinely nothing to act on, and saying why
                  beats an empty cell. */}
              <div className="template-field-actions">
                {isEditing && !hiddenByMaster ? (
                  <>
                    {(field.type === "template" || field.type === "certificate") && (
                      <button
                        className={`btn-toggle ${field.mandatory ? "btn-toggle-mandatory-on" : "btn-toggle-off"}`}
                        title="Mandatory"
                        onClick={() =>
                          updateInheritedField(field.key, { mandatory: !field.mandatory })
                        }
                      >M</button>
                    )}
                    {(field.type === "template" || field.type === "certificate") && (
                      <button
                        className={`btn-toggle ${field.approvalRequired ? "btn-toggle-approval-on" : "btn-toggle-off"}`}
                        title="Approval Required"
                        onClick={() =>
                          updateInheritedField(field.key, {
                            approvalRequired: !field.approvalRequired,
                          })
                        }
                      >✔</button>
                    )}
                    {field.type === "template" && (
                      <button
                        className={`btn-toggle ${field.countersignRequired ? "btn-toggle-countersign-on" : "btn-toggle-off"}`}
                        title="Countersign Required"
                        onClick={() =>
                          updateInheritedField(field.key, {
                            countersignRequired: !field.countersignRequired,
                          })
                        }
                      >✎</button>
                    )}
                    {/* The delete-equivalent. An inherited line can't actually
                        be removed — it belongs to the Master — so dropping it
                        here hides it for this service and nowhere else. */}
                    <button
                      className={`btn-toggle ${field.hidden ? "btn-toggle-delete" : "btn-toggle-off"}`}
                      title={
                        field.hidden
                          ? "Hidden here — click to show again"
                          : "Hide for this service only"
                      }
                      onClick={() =>
                        updateInheritedField(field.key, { hidden: !field.hidden })
                      }
                    >
                      H
                    </button>
                  </>
                ) : (
                  <div className="manage-input smrtdocs-requirements-text" style={{ width: "50ch", cursor: "not-allowed", opacity: 0.85 }}>
                    {hiddenByMaster
                      ? "Hidden on the Master"
                      : [field.mandatory ? "Mandatory" : null, field.approvalRequired ? "Approval Required" : null, field.countersignRequired ? "Countersign Required" : null].filter(Boolean).join(", ") || "—"}
                  </div>
                )}
              </div>
            </div>
            );
          })}
          {/* Local fields attached to this template step */}
          {!collapsed[step.id] &&
            (localTemplate?.attachedFields?.[step.id] || []).map((field: any) => (
            <React.Fragment key={field.key}>
              {renderLocalField(field, step.id, true)}
            </React.Fragment>
          ))}
          {isEditing && !collapsed[step.id] && (
          <div className="template-field-row add-field-row">
            <div className="add-field-cell">
              <button className="btn-add btn-add-row" onClick={() => addLocalFieldToStep(step.id)}>
                + Add Document
              </button>
            </div>
          </div>
          )}
        </div>
      ))}

      {/* Local-only steps (local mode) */}
      {isLocalMode && localTemplate?.steps?.map((step: any) => (
        <div key={step.id} className="template-card" style={{ marginBottom: "16px" }}>
          <div className="template-card-header">
            <button
              className="td-collapse-toggle"
              title={collapsed[step.id] ? "Expand category" : "Collapse category"}
              aria-expanded={!collapsed[step.id]}
              onClick={() => toggleCollapsed(step.id)}
            >
              {collapsed[step.id] ? "▸" : "▾"}
            </button>
            <input
              className="manage-input"
              value={step.label}
              disabled={!isEditing}
              onChange={(e: any) => {
                const updated = { ...localTemplate };
                updated.steps = updated.steps.map((s: any) => s.id === step.id ? { ...s, label: e.target.value } : s);
                setLocalTemplate(updated);
              }}
            />
            {collapsed[step.id] && (
              <span className="td-collapsed-count">
                {(step.fields || []).length}{" "}
                {(step.fields || []).length === 1 ? "document" : "documents"}
              </span>
            )}
            {isEditing && (
            <div>
              <button className="btn-move" onClick={() => {
                const updated = { ...localTemplate };
                const steps = [...updated.steps];
                const index = steps.findIndex((s: any) => s.id === step.id);
                if (index === 0) return;
                [steps[index], steps[index - 1]] = [steps[index - 1], steps[index]];
                updated.steps = steps;
                setLocalTemplate(updated);
              }}>↑</button>
              <button className="btn-move" onClick={() => {
                const updated = { ...localTemplate };
                const steps = [...updated.steps];
                const index = steps.findIndex((s: any) => s.id === step.id);
                if (index === steps.length - 1) return;
                [steps[index], steps[index + 1]] = [steps[index + 1], steps[index]];
                updated.steps = steps;
                setLocalTemplate(updated);
              }}>↓</button>
              <button className="edit-icon" onClick={() => {
                const updated = { ...localTemplate, steps: localTemplate.steps.filter((s: any) => s.id !== step.id) };
                setLocalTemplate(updated);
              }}>🗑️</button>
            </div>
            )}
          </div>
          {!collapsed[step.id] && (
          <div className="template-grid-header">
            <div>Document</div>
            <div>Type</div>
            <div>Actions</div>
          </div>
          )}
          {!collapsed[step.id] && step.fields.map((field: any) => (
            <React.Fragment key={field.key}>
              {renderLocalField(field, step.id, false)}
            </React.Fragment>
          ))}
          {isEditing && !collapsed[step.id] && (
          <div className="template-field-row add-field-row">
            <div className="add-field-cell">
              <button className="btn-add btn-add-row" onClick={() => {
                const updated = { ...localTemplate };
                updated.steps = updated.steps.map((s: any) => {
                  if (s.id !== step.id) return s;
                  return {
                    ...s,
                    fields: [...s.fields, {
                      key: `ldoc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                      label: "New Document",
                      type: "",
                      mandatory: false,
                      approvalRequired: false,
                      countersignRequired: false,
                    }],
                  };
                });
                setLocalTemplate(updated);
              }}>+ Add Document</button>
            </div>
          </div>
          )}
        </div>
      ))}

      {/* Add local category button (local mode) */}
      {isLocalMode && isEditing && (
        <div className="add-step-container" style={{ marginTop: "16px" }}>
          <button className="btn-add btn-add-step" onClick={() => {
            const nextId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
            setLocalTemplate({
              ...localTemplate,
              steps: [...(localTemplate?.steps || []), { id: nextId, label: "New Category", fields: [] }],
            });
          }}>+ Add Category</button>
        </div>
      )}

      {/* Normal template editing (non-local mode) */}
      {!isLocalMode && template.steps.map((step: any) => (
        <div
          key={step.id}
          className="template-card"
          style={{ marginBottom: "16px" }}
        >
          <div className="template-card-header">
            {/* Expand / collapse this category. Collapse them all to reduce the
                builder to a category-only list for reordering. */}
            <button
              className="td-collapse-toggle"
              title={collapsed[step.id] ? "Expand category" : "Collapse category"}
              aria-expanded={!collapsed[step.id]}
              onClick={() => toggleCollapsed(step.id)}
            >
              {collapsed[step.id] ? "▸" : "▾"}
            </button>

            <input
              className="manage-input"
              value={step.label}
              disabled={!isEditing}
              onChange={(e: any) => {
                const updated = { ...template };
                updated.steps = updated.steps.map((s: any) =>
                  s.id === step.id ? { ...s, label: e.target.value } : s,
                );
                setTemplate(updated);
              }}
            />

            {collapsed[step.id] && (
              <span className="td-collapsed-count">
                {step.fields.length}{" "}
                {step.fields.length === 1 ? "document" : "documents"}
              </span>
            )}

            {isEditing && (
              <div>
                <button
                  className="btn-move"
                  onClick={() => {
                    const index = template.steps.findIndex(
                      (s: any) => s.id === step.id,
                    );
                    if (index === 0) return;

                    const updated = { ...template };
                    const steps = [...updated.steps];
                    [steps[index], steps[index - 1]] = [
                      steps[index - 1],
                      steps[index],
                    ];

                    updated.steps = steps.map((s: any, i: number) => ({
                      ...s,
                      id: i + 1,
                    }));

                    setTemplate(updated);
                    swapCollapsed(index, index - 1);
                  }}
                >
                  ↑
                </button>

                <button
                  className="btn-move"
                  onClick={() => {
                    const index = template.steps.findIndex(
                      (s: any) => s.id === step.id,
                    );
                    if (index === template.steps.length - 1) return;

                    const updated = { ...template };
                    const steps = [...updated.steps];
                    [steps[index], steps[index + 1]] = [
                      steps[index + 1],
                      steps[index],
                    ];

                    updated.steps = steps.map((s: any, i: number) => ({
                      ...s,
                      id: i + 1,
                    }));

                    setTemplate(updated);
                    swapCollapsed(index, index + 1);
                  }}
                >
                  ↓
                </button>
                <button
                  className="edit-icon"
                  onClick={() => {
                    const updated = {
                      ...template,
                      steps: template.steps.filter(
                        (s: any) => s.id !== step.id,
                      ),
                    };
                    setTemplate(updated);
                  }}
                >
                  🗑️
                </button>
              </div>
            )}
          </div>
          {!collapsed[step.id] && (
          <div className="template-grid-header td-has-handle">
            <div className="td-handle-col" />
            <div>Document</div>
            <div>Type</div>
            <div>{isEditing ? "Actions" : "Status Requirements"}</div>
          </div>
          )}

          {!collapsed[step.id] && step.fields.map((field: any) => (
            <div
              key={field.key}
              className={`template-field-row td-has-handle${
                dragOverKey === field.key && dragStepId === step.id
                  ? dragOverPos === "after"
                    ? " td-drag-over-after"
                    : " td-drag-over-before"
                  : ""
              }`}
              onDragOver={(e) => {
                if (
                  dragKey &&
                  dragStepId === step.id &&
                  dragKey !== field.key
                ) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pos =
                    e.clientY > rect.top + rect.height / 2 ? "after" : "before";
                  if (dragOverKey !== field.key) setDragOverKey(field.key);
                  if (dragOverPos !== pos) setDragOverPos(pos);
                }
              }}
              onDrop={(e) => {
                if (
                  dragKey &&
                  dragStepId === step.id &&
                  dragKey !== field.key
                ) {
                  e.preventDefault();
                  reorderField(
                    step.id,
                    dragKey,
                    field.key,
                    dragOverPos === "after",
                  );
                }
                clearDrag();
              }}
              onDragLeave={() => {
                if (dragOverKey === field.key) setDragOverKey(null);
              }}
            >
              {/* Drag handle — grab to reorder this document within its
                  category. Rendered as an inert spacer when not editing so the
                  grid columns stay aligned with the header. */}
              {isEditing ? (
                <span
                  className="td-drag-handle"
                  draggable
                  title="Drag to reorder"
                  onDragStart={(e) => {
                    setDragKey(field.key);
                    setDragStepId(step.id);
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", field.key);
                  }}
                  onDragEnd={clearDrag}
                >
                  ⋮
                </span>
              ) : (
                <div className="td-handle-col" />
              )}

              {/* A hidden line is greyed and struck through rather than just
                  flagged — it reads as withdrawn at a glance. Dimmed cell by
                  cell, never on the row: opacity on a parent composites its
                  children with it, and the H button is the way back. */}
              <input
                className="manage-input"
                value={field.label}
                disabled={!isEditing}
                style={{
                  textDecoration: field.hidden ? "line-through" : "none",
                  opacity: field.hidden ? 0.5 : 1,
                }}
                onChange={(e: any) => {
                  const updated = { ...template };
                  updated.steps = updated.steps.map((s: any) =>
                    s.id !== step.id
                      ? s
                      : {
                          ...s,
                          fields: s.fields.map((f: any) =>
                            f.key === field.key
                              ? { ...f, label: e.target.value }
                              : f,
                          ),
                        },
                  );
                  setTemplate(updated);
                }}
              />

              {isEditing ? (
                <select
                  className="manage-input"
                  value={field.type || ""}
                  style={{ opacity: field.hidden ? 0.5 : 1 }}
                  onChange={(e: any) => {
                    const updated = { ...template };
                    updated.steps = updated.steps.map((s: any) =>
                      s.id !== step.id
                        ? s
                        : {
                            ...s,
                            fields: s.fields.map((f: any) =>
                              f.key === field.key
                                ? { ...f, type: e.target.value }
                                : f,
                            ),
                          },
                    );
                    setTemplate(updated);
                  }}
                >
                  <option value="">- Please Select -</option>
                  <option value="info">Info</option>
                  <option value="certificate">Certificate</option>
                  <option value="template">Template</option>
                </select>
              ) : (
                <div className="manage-input">
                  {field.type
                    ? field.type.charAt(0).toUpperCase() + field.type.slice(1)
                    : "—"}
                </div>
              )}

              {!isEditing && (
                <div className="template-field-actions">
                  <div
                    className="manage-input smrtdocs-requirements-text"
                    style={{ width: "50ch" }}
                  >
                    {[
                      field.mandatory ? "Mandatory" : null,
                      field.approvalRequired ? "Approval Required" : null,
                      field.countersignRequired ? "Countersign Required" : null,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </div>
                </div>
              )}
              {isEditing && (
                <div className="template-field-actions">
                  {/* Mandatory */}
                  {(field.type === "template" ||
                    field.type === "certificate") && (
                    <button
                      className={`btn-toggle ${
                        field.mandatory
                          ? "btn-toggle-mandatory-on"
                          : "btn-toggle-off"
                      }`}
                      title="Mandatory"
                      onClick={() => {
                        const updated = { ...template };
                        updated.steps = updated.steps.map((s: any) =>
                          s.id !== step.id
                            ? s
                            : {
                                ...s,
                                fields: s.fields.map((f: any) =>
                                  f.key === field.key
                                    ? { ...f, mandatory: !f.mandatory }
                                    : f,
                                ),
                              },
                        );
                        setTemplate(updated);
                      }}
                    >
                      M
                    </button>
                  )}

                  {/* Approval */}
                  {(field.type === "template" ||
                    field.type === "certificate") && (
                    <button
                      className={`btn-toggle ${
                        field.approvalRequired
                          ? "btn-toggle-approval-on"
                          : "btn-toggle-off"
                      }`}
                      title="Approval Required"
                      onClick={() => {
                        const updated = { ...template };
                        updated.steps = updated.steps.map((s: any) =>
                          s.id !== step.id
                            ? s
                            : {
                                ...s,
                                fields: s.fields.map((f: any) =>
                                  f.key === field.key
                                    ? {
                                        ...f,
                                        approvalRequired: !f.approvalRequired,
                                      }
                                    : f,
                                ),
                              },
                        );
                        setTemplate(updated);
                      }}
                    >
                      ✔
                    </button>
                  )}

                  {/* Countersign */}
                  {field.type === "template" && (
                    <button
                      className={`btn-toggle ${
                        field.countersignRequired
                          ? "btn-toggle-countersign-on"
                          : "btn-toggle-off"
                      }`}
                      title="Countersign Required"
                      onClick={() => {
                        const updated = { ...template };
                        updated.steps = updated.steps.map((s: any) =>
                          s.id !== step.id
                            ? s
                            : {
                                ...s,
                                fields: s.fields.map((f: any) =>
                                  f.key === field.key
                                    ? {
                                        ...f,
                                        countersignRequired:
                                          !f.countersignRequired,
                                      }
                                    : f,
                                ),
                              },
                        );
                        setTemplate(updated);
                      }}
                    >
                      ✎
                    </button>
                  )}

                  {/* Hide. On a Master this reaches every Sub Service under it
                      — the line stops being asked for anywhere. Kept as a flag
                      rather than a delete so it can come back, and so the
                      documents already filed against it aren't orphaned. */}
                  <button
                    className={`btn-toggle ${
                      field.hidden ? "btn-toggle-delete" : "btn-toggle-off"
                    }`}
                    title={
                      field.hidden
                        ? "Hidden — click to show again"
                        : "Hide Document"
                    }
                    onClick={() => {
                      const updated = { ...template };
                      updated.steps = updated.steps.map((s: any) =>
                        s.id !== step.id
                          ? s
                          : {
                              ...s,
                              fields: s.fields.map((f: any) =>
                                f.key === field.key
                                  ? { ...f, hidden: !f.hidden }
                                  : f,
                              ),
                            },
                      );
                      setTemplate(updated);
                    }}
                  >
                    H
                  </button>

                  {/* Reordering moved to the ⋮ drag handle on the left of the
                      row — the old ↑/↓ buttons only moved one place per click. */}

                  {/* Delete */}
                  <button
                    className="btn-toggle btn-toggle-delete"
                    title="Delete"
                    onClick={() => {
                      const updated = { ...template };
                      updated.steps = updated.steps.map((s: any) => {
                        if (s.id !== step.id) return s;
                        return {
                          ...s,
                          fields: s.fields.filter(
                            (f: any) => f.key !== field.key,
                          ),
                        };
                      });
                      setTemplate(updated);
                    }}
                  >
                    🗑
                  </button>
                </div>
              )}
            </div>
          ))}

          {isEditing && !collapsed[step.id] && (
            <div className="template-field-row add-field-row">
              <div className="add-field-cell">
                <button
                  className="btn-add btn-add-row"
                  onClick={() => {
                    const updated = { ...template };
                    updated.steps = updated.steps.map((s: any) => {
                      if (s.id !== step.id) return s;

                      return {
                        ...s,
                        fields: [
                          ...s.fields,
                          {
                            key: `doc_${Date.now()}`,
                            label: "New Document",
                            type: "",
                            mandatory: false,
                            approvalRequired: false,
                            countersignRequired: false,
                          },
                        ],
                      };
                    });
                    setTemplate(updated);
                  }}
                >
                  + Add Document
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {!isLocalMode && isEditing && (
        <div className="add-step-container" style={{ marginTop: "16px" }}>
          <button
            className="btn-add btn-add-step"
            onClick={() => {
              const nextId =
                template.steps.length > 0
                  ? Math.max(...template.steps.map((s: any) => s.id)) + 1
                  : 1;

              setTemplate({
                ...template,
                steps: [
                  ...template.steps,
                  { id: nextId, label: "New Category", fields: [] },
                ],
              });
            }}
          >
            + Add Category
          </button>
        </div>
      )}
    </div>
  );
}
